import { Component, inject, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { forkJoin, switchMap, takeUntil } from 'rxjs';
import { Router } from '@angular/router';

import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';

import { ContainerSasUrl } from '../../../../shared/interfaces/company';

import { ImageService } from '../../../../shared/services/image-service/image.service';

import { ProfileService } from '../../services/profile.service';
import { CdfiProfile } from '../../models/profile.model';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { RegionDetailsComponent } from '../region-details/region-details.component';
import { AuthService } from '../../../auth/services/auth.service';

@Component({
  selector: 'app-cdfi-profile-details',
  templateUrl: './cdfi-profile-details.component.html',
  styleUrls: ['./cdfi-profile-details.component.scss'],
  standalone: true,
  imports: [SvgIconComponent, RegionDetailsComponent],
})
export class CdfiProfileDetailsComponent
  extends MenuComponentBase
  implements OnInit
{
  profileService = inject(ProfileService);
  imageService = inject(ImageService);
  router = inject(Router);
  private location = inject(Location);
  private authService = inject(AuthService);

  readonly isAnonymous = this.route.snapshot.data['anonymous'] === true;
  readonly isSmeUser = !this.isAnonymous && this.authService.isSMEUser();
  cdfiProfileDetails!: CdfiProfile;
  connectionName!: string;
  containerSasUrl!: ContainerSasUrl;
  isRegionModalOpen = false;
  regionModalDefaultTab = 'states';
  isLoading = false;
  isNotFound = false;
  preparationSteps = [
    'Confirm your business record has current address and ownership details',
    'Add requested loan amount, use of funds, and timeline',
    'Verify the Organization serves your location and industry',
    'Review their supported products and funding criteria',
  ];

  readonly stitchLoanProducts = [
    {
      feature: 'Amount',
      termLoan: '$10,000 – $500,000',
      lineOfCredit: '$10,000 – $150,000',
    },
    {
      feature: 'Interest Rates',
      termLoan: '13.49 – 35.99%',
      lineOfCredit: '1.35 – 2.59% monthly fee on drawn funds',
    },
    {
      feature: 'Term',
      termLoan: '12–60 months',
      lineOfCredit: '18–24 months',
    },
    {
      feature: 'Repayment Frequency',
      termLoan: 'Monthly or Semi-monthly',
      lineOfCredit: 'Monthly',
    },
    {
      feature: 'Security',
      termLoan: 'UCC-1; Personal Guarantee',
      lineOfCredit: 'UCC-1; Personal Guarantee',
    },
    {
      feature: 'Fees',
      termLoan: '3% origination fee; Late fees may apply',
      lineOfCredit: '3% commitment fee; Late and inactivity fees may apply',
    },
    {
      feature: 'Prepayment Penalties',
      termLoan: 'No',
      lineOfCredit: 'No',
    },
    {
      feature: 'Credit Reporting',
      termLoan:
        'Performance reported to commercial bureau; consumer reporting only if personal guarantor defaults',
      lineOfCredit:
        'Performance reported to commercial bureau; consumer reporting only if personal guarantor defaults',
    },
  ];

  readonly stitchUnderwritingCriteria = [
    {
      category: 'Time in Business',
      details: '12+ months (6+ months if cash flow is strong)',
    },
    {
      category: 'Revenue',
      details: '$50,000+ trailing 12-month gross income',
    },
    {
      category: 'Credit',
      details: '640+ personal score for the primary guarantor',
    },
  ];

  readonly stitchEligibilityExceptions = [
    'Nonprofit organizations.',
    'Businesses organized in Nevada.',
    'Sole proprietorships based in Michigan, Nebraska, New York, and West Virginia.',
    'Businesses owned by U.S. citizens who do not maintain a U.S.-based primary residence, businesses owned by non-U.S. citizens who are not permanent residents (Green Card holders), businesses owned by a borrower without an SSN.',
    'Businesses in high-risk industries that pose ethical or compliance concerns.',
  ];

  get isStitch(): boolean {
    return (
      this.connectionName?.trim().toLowerCase() === 'stitch' ||
      this.cdfiProfileDetails?.connection_name?.trim().toLowerCase() ===
        'stitch'
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    window.scrollTo(0, 0);
    this.isLoading = true;
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          this.connectionName = params.get('connection_name') || '';
          const detailsRequest = this.isAnonymous
            ? this.profileService.getAnonymousCdfiProfileDetails(
                this.connectionName,
              )
            : this.profileService.getCdfiProfileDetails(this.connectionName);

          return forkJoin({
            details: detailsRequest,
            sasUrl: this.imageService.getContainerSasUrl(),
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ details, sasUrl }) => {
          this.containerSasUrl = sasUrl;
          this.cdfiProfileDetails = details;

          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.isNotFound = true;
        },
      });
  }

  getImageUrl(path?: string | null): string {
    if (!this.containerSasUrl || !path) {
      return '';
    }

    return `${path}?${this.containerSasUrl.sas_token}`;
  }

  getWebsiteUrl(website?: string | null): string {
    if (!website) {
      return '';
    }

    return website.startsWith('http') ? website : `https://${website}`;
  }

  getDescriptionText(description?: string | null): string {
    if (!description) {
      return 'Contact this Organization to learn more about their lending programs and support services.';
    }

    return description
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  formatCurrency(value?: string | number | null): string {
    const amount = Number(value ?? 0);

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(Number.isFinite(amount) ? amount : 0);
  }

  formatCount(value?: number | null): string {
    return new Intl.NumberFormat('en-US').format(value ?? 0);
  }

  get hasFundingActivity(): boolean {
    return Number(this.cdfiProfileDetails?.total_funded_amount) > 0;
  }

  getSupportedProducts(): string[] {
    return (
      this.cdfiProfileDetails?.supported_products
        ?.filter(Boolean)
        .map((product) =>
          product
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase()),
        ) ?? []
    );
  }

  getSupportedProductsCount(): number {
    return (
      this.cdfiProfileDetails?.supported_products?.filter(Boolean).length ?? 0
    );
  }

  getSupportedRegionCounts(): { type: string; label: string }[] {
    const regions = this.cdfiProfileDetails?.supported_regions;

    if (!regions) {
      return [];
    }

    const counts = [];
    if (regions.states?.length) {
      counts.push({
        type: 'states',
        label: `${this.formatCount(regions.states.length)} ${regions.states.length === 1 ? 'State' : 'States'}`,
      });
    }
    if (regions.counties?.length) {
      counts.push({
        type: 'counties',
        label: `${this.formatCount(regions.counties.length)} ${regions.counties.length === 1 ? 'County' : 'Counties'}`,
      });
    }
    if (regions.cities?.length) {
      counts.push({
        type: 'cities',
        label: `${this.formatCount(regions.cities.length)} ${regions.cities.length === 1 ? 'City' : 'Cities'}`,
      });
    }
    if (regions.zip_codes?.length) {
      counts.push({
        type: 'zip_codes',
        label: `${this.formatCount(regions.zip_codes.length)} ZIP codes`,
      });
    }

    if (counts.length === 0 && regions.nationwide_support) {
      counts.push({ type: 'nationwide', label: 'Nationwide' });
    }

    return counts;
  }

  isOnlyNationwide(): boolean {
    const counts = this.getSupportedRegionCounts();
    return counts.length === 1 && counts[0].type === 'nationwide';
  }

  getServiceRegionCount(): number {
    const regions = this.cdfiProfileDetails?.supported_regions;

    if (!regions) {
      return 0;
    }

    return (
      (regions.states?.length || 0) +
      (regions.counties?.length || 0) +
      (regions.cities?.length || 0) +
      (regions.zip_codes?.length || 0)
    );
  }

  openRegionModal(): void {
    this.isRegionModalOpen = true;
  }

  closeRegionModal(): void {
    this.isRegionModalOpen = false;
  }

  goBackToCdfis(): void {
    if (this.isAnonymous) {
      this.router.navigate(['/cdfis/public']);
      return;
    }

    this.location.back();
  }

  goToBusinessRecords(): void {
    this.router.navigate(['/business']);
  }
}
